import { useCallback, useContext, useEffect, useState } from "preact/hooks";
import { ComponentChildren, createContext } from "preact";

import { createChat, fetchChats } from "@api/chat";
import { ChatMeta } from "@interfaces/chat";
import { transformChats } from "@transformers/chat";
import { transformChatEvent } from "@transformers/chat-actions";

interface Meta {
  ready: boolean;
  chats: ChatMeta[];
  newChat: (message: string) => Promise<(string | null)[]>;
}

interface ProviderProps {
  children: ComponentChildren;
}

const ChatContext = createContext<Meta | null>(null);

export function ChatProvider({ children }: ProviderProps) {
  const [ready, setReady] = useState(false);
  const [chats, setChats] = useState<ChatMeta[]>([]);

  const load = useCallback(async () => {
    const data = transformChats(
      await fetchChats()
    );

    setChats(data);
    setReady(true);
  }, []);

  const newChat = useCallback(async (message: string) => {
    try {
      const data = transformChatEvent(
        await createChat(message)
      );

      if (data.success)
        return [data.uuid, null];

      return [null, data.error];
    } catch (e) {
      return [null, "Something went wrong. Please try again."];
    }
  }, []);

  useEffect(() => {
    load();
  }, []);

  const value = {
    ready,
    chats,
    newChat,
  };

  return <ChatContext.Provider value={value}>
    {children}
  </ChatContext.Provider>
}

export function useChat(): Meta {
  return useContext(ChatContext)!;
}

